import { useState } from 'react';
import { Plus, Trash2, GripVertical, Save, Loader2, Pencil, X, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { useActivityTemplates, type ActivityTemplate } from '@/data/hooks/useActivityTemplates';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { DEMO_TYPE_LABELS } from '@/types';
import type { ActivityType } from '@/types';
import { toast } from 'sonner';

const ALL_ACTIVITY_TYPES: { value: ActivityType; label: string }[] = [
  ...Object.entries(DEMO_TYPE_LABELS).map(([k, v]) => ({ value: k as ActivityType, label: v })),
  { value: 'text_sent', label: 'Text Sent' },
  { value: 'email_sent', label: 'Email Sent' },
  { value: 'note_added', label: 'Note Added' },
];

export function ActivityTemplateManager() {
  const { mode, dealershipId, role } = useAuth();
  const { templates, loading, refresh } = useActivityTemplates({ activeOnly: false });
  const [editing, setEditing] = useState<Record<string, { name: string; activity_types: ActivityType[] } | undefined>>({});
  const [newName, setNewName] = useState('');
  const [newTypes, setNewTypes] = useState<ActivityType[]>([]);
  const [busy, setBusy] = useState(false);

  const isManager = role === 'manager' || role === 'admin';
  if (!isManager) return null;

  const isReal = mode === 'real' && !!dealershipId;

  const startEdit = (t: ActivityTemplate) => {
    setEditing((s) => ({ ...s, [t.id]: { name: t.name, activity_types: [...t.activity_types] } }));
  };
  const cancelEdit = (id: string) => setEditing((s) => ({ ...s, [id]: undefined }));

  const toggleType = (list: ActivityType[], type: ActivityType) =>
    list.includes(type) ? list.filter((t) => t !== type) : [...list, type];

  const saveEdit = async (t: ActivityTemplate) => {
    const draft = editing[t.id];
    if (!draft) return;
    if (!draft.name.trim()) { toast.error('Template name required'); return; }
    if (draft.activity_types.length === 0) { toast.error('Pick at least one activity'); return; }
    if (!isReal) { toast.info('Editing templates is available in Real mode only.'); return; }
    setBusy(true);
    const { error } = await supabase
      .from('activity_templates')
      .update({ name: draft.name.trim(), activity_types: draft.activity_types })
      .eq('id', t.id);
    setBusy(false);
    if (error) { toast.error('Failed to save', { description: error.message }); return; }
    toast.success('Template updated');
    cancelEdit(t.id);
    refresh();
  };

  const toggleActive = async (t: ActivityTemplate, value: boolean) => {
    if (!isReal) { toast.info('Editing templates is available in Real mode only.'); return; }
    const { error } = await supabase
      .from('activity_templates')
      .update({ is_active: value })
      .eq('id', t.id);
    if (error) { toast.error('Failed to toggle', { description: error.message }); return; }
    refresh();
  };

  const deleteTemplate = async (t: ActivityTemplate) => {
    if (!isReal) return;
    if (!confirm(`Delete template "${t.name}"?`)) return;
    const { error } = await supabase.from('activity_templates').delete().eq('id', t.id);
    if (error) { toast.error('Failed to delete', { description: error.message }); return; }
    toast.success('Template deleted');
    refresh();
  };

  const addTemplate = async () => {
    if (!newName.trim()) { toast.error('Template name required'); return; }
    if (newTypes.length === 0) { toast.error('Pick at least one activity'); return; }
    if (!isReal) { toast.info('Adding templates is available in Real mode only.'); return; }
    setBusy(true);
    const nextSort = (templates[templates.length - 1]?.sort_order ?? -1) + 1;
    const { error } = await supabase.from('activity_templates').insert({
      dealership_id: dealershipId!,
      name: newName.trim(),
      activity_types: newTypes,
      sort_order: nextSort,
      is_active: true,
    });
    setBusy(false);
    if (error) { toast.error('Failed to add template', { description: error.message }); return; }
    toast.success('Template added');
    setNewName('');
    setNewTypes([]);
    refresh();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Activity Templates</CardTitle>
        <CardDescription>One-tap chips reps see on Quick Log and Deal pages. Each fires multiple activities at once.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading templates…
          </div>
        ) : templates.length === 0 ? (
          <p className="text-sm text-muted-foreground">No templates yet. Add one below.</p>
        ) : (
          <ul className="space-y-2">
            {templates.map((t) => {
              const draft = editing[t.id];
              return (
                <li key={t.id} className="rounded-lg border border-border p-3 space-y-2">
                  <div className="flex items-start gap-3">
                    <GripVertical className="h-4 w-4 text-muted-foreground mt-1 shrink-0" />
                    <div className="flex-1 space-y-2 min-w-0">
                      {draft ? (
                        <Input
                          value={draft.name}
                          onChange={(e) => setEditing((s) => ({ ...s, [t.id]: { ...draft, name: e.target.value } }))}
                          className="h-8"
                        />
                      ) : (
                        <p className="font-medium text-sm">{t.name}</p>
                      )}
                      <div className="flex flex-wrap gap-1">
                        {ALL_ACTIVITY_TYPES.map((opt) => {
                          const list = draft ? draft.activity_types : t.activity_types;
                          const selected = list.includes(opt.value);
                          if (!draft && !selected) return null;
                          return (
                            <button
                              key={opt.value}
                              type="button"
                              disabled={!draft}
                              onClick={() => draft && setEditing((s) => ({ ...s, [t.id]: { ...draft, activity_types: toggleType(draft.activity_types, opt.value) } }))}
                              className={`px-2 py-0.5 rounded-full text-[11px] border transition-colors ${
                                selected
                                  ? 'bg-primary/15 border-primary/40 text-primary'
                                  : 'border-border text-muted-foreground hover:bg-muted'
                              } ${!draft ? 'cursor-default' : ''}`}
                            >
                              {opt.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {!draft && (
                        <Switch
                          checked={t.is_active}
                          onCheckedChange={(v) => toggleActive(t, v)}
                          aria-label="Active"
                        />
                      )}
                      {draft ? (
                        <>
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => saveEdit(t)} disabled={busy}>
                            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                          </Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => cancelEdit(t.id)}>
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => startEdit(t)}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => deleteTemplate(t)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  {!t.is_active && !draft && (
                    <Badge variant="outline" className="text-[10px]">Inactive</Badge>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {/* Add new */}
        <div className="rounded-lg border border-dashed border-border p-3 space-y-3">
          <p className="text-sm font-medium flex items-center gap-2"><Plus className="h-4 w-4" /> Add template</p>
          <Input
            placeholder="e.g. Walkaround + Text Sent"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <div className="flex flex-wrap gap-1">
            {ALL_ACTIVITY_TYPES.map((opt) => {
              const selected = newTypes.includes(opt.value);
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setNewTypes((s) => toggleType(s, opt.value))}
                  className={`px-2 py-0.5 rounded-full text-[11px] border transition-colors ${
                    selected ? 'bg-primary/15 border-primary/40 text-primary' : 'border-border text-muted-foreground hover:bg-muted'
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          <Button size="sm" onClick={addTemplate} disabled={busy} className="gap-2">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Add template
          </Button>
          {!isReal && (
            <p className="text-xs text-muted-foreground">Switch to Real mode to manage templates.</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}